<script setup lang="ts">
const { customer, isLoggedIn, logout } = useAuth()

onMounted(() => {
  if (!isLoggedIn.value) {
    navigateTo('/account/login')
  }
})

async function handleLogout() {
  await logout()
  navigateTo('/')
}

const { data: ordersData, pending: ordersPending } = useFetch('/api/customer/orders', { server: false })
const { data: addressesData, pending: addressesPending } = useFetch('/api/customer/addresses', { server: false })

const orders = computed(() => (ordersData.value as any[]) ?? [])
const addresses = computed(() => (addressesData.value as any[]) ?? [])

const recentOrders = computed(() => orders.value.slice(0, 3))

const totalOrders = computed(() => orders.value.length)
const totalSpent = computed(() => {
  const sum = orders.value.reduce((acc: number, o: any) => {
    return acc + parseFloat(o.currentTotalPrice?.amount ?? '0')
  }, 0)
  return sum.toFixed(2)
})

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function toBase64url(str: string): string {
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function orderLink(order: any) {
  return `/account/orders/${toBase64url(order.id)}`
}

function addressLabel(addr: any) {
  return addr.isDefault ? 'Home (Default)' : 'Address'
}

function addressCity(addr: any) {
  const parts = [addr.city, addr.province, addr.zip].filter(Boolean)
  return parts.join(', ')
}

function statusBg(status: string) {
  return status === 'Processing' ? 'bg-surface' : 'bg-raised'
}
</script>

<template>
  <div class="bg-page flex flex-col items-start">
    <!-- Page Header -->
    <div class="flex flex-col gap-2 items-start pb-10 pt-14 px-4 md:px-20 w-full">
      <p class="font-sans text-base text-secondary">Welcome back,</p>
      <p class="font-serif font-semibold text-4xl md:text-5xl text-dark">
        {{ customer?.firstName }} {{ customer?.lastName }}
      </p>
    </div>

    <!-- Main Layout -->
    <div class="flex flex-col md:flex-row gap-12 items-start pb-24 px-4 md:px-20 w-full">
      <!-- Sidebar -->
      <div class="flex md:flex-col gap-1 items-start shrink-0 w-full md:w-[220px] overflow-x-auto">
        <div class="bg-surface flex items-center px-4 py-3 rounded-lg shrink-0">
          <p class="font-sans font-medium text-[15px] text-dark whitespace-nowrap">Dashboard</p>
        </div>
        <NuxtLink to="/account/orders" class="flex items-center px-4 py-3 rounded-lg shrink-0 hover:bg-surface transition-colors">
          <p class="font-sans text-[15px] text-secondary whitespace-nowrap">Order History</p>
        </NuxtLink>
        <NuxtLink to="/account/addresses" class="flex items-center px-4 py-3 rounded-lg shrink-0 hover:bg-surface transition-colors">
          <p class="font-sans text-[15px] text-secondary whitespace-nowrap">Addresses</p>
        </NuxtLink>
        <NuxtLink to="/account/details" class="flex items-center px-4 py-3 rounded-lg shrink-0 hover:bg-surface transition-colors">
          <p class="font-sans text-[15px] text-secondary whitespace-nowrap">Account Details</p>
        </NuxtLink>
        <button class="flex items-center px-4 py-3 rounded-lg shrink-0 hover:bg-surface transition-colors" @click="handleLogout">
          <p class="font-sans text-[15px] text-secondary whitespace-nowrap">Log Out</p>
        </button>
      </div>

      <!-- Main Content -->
      <div class="flex flex-1 flex-col gap-8 items-start min-w-0 w-full">
        <!-- Stats -->
        <div class="flex flex-col md:flex-row gap-5 items-start w-full">
          <div class="bg-surface flex flex-1 flex-col gap-2 items-start p-6 rounded-xl w-full md:w-auto">
            <p class="font-serif font-semibold text-4xl text-dark">
              <span v-if="ordersPending" class="text-muted">—</span>
              <span v-else>{{ totalOrders }}</span>
            </p>
            <p class="font-sans text-sm text-secondary">Total Orders</p>
          </div>
          <div class="bg-surface flex flex-1 flex-col gap-2 items-start p-6 rounded-xl w-full md:w-auto">
            <p class="font-serif font-semibold text-4xl text-dark">
              <span v-if="ordersPending" class="text-muted">—</span>
              <span v-else>${{ totalSpent }}</span>
            </p>
            <p class="font-sans text-sm text-secondary">Total Spent</p>
          </div>
        </div>

        <!-- Recent Orders -->
        <div class="bg-surface flex flex-col items-start p-7 rounded-xl w-full">
          <div class="flex items-center justify-between w-full">
            <p class="font-serif font-semibold text-[22px] text-dark">Recent Orders</p>
            <NuxtLink to="/account/orders" class="font-sans font-medium text-sm text-brand">View All →</NuxtLink>
          </div>
          <div class="h-5" />

          <div v-if="ordersPending" class="flex items-center justify-center py-8 w-full">
            <p class="font-sans text-sm text-muted">Loading orders…</p>
          </div>
          <div v-else-if="recentOrders.length === 0" class="flex items-center justify-center py-8 w-full">
            <p class="font-sans text-sm text-muted">No orders yet.</p>
          </div>
          <template v-else>
            <div
              v-for="(order, index) in recentOrders"
              :key="order.id"
              class="flex items-center justify-between py-4 w-full"
              :class="{ 'border-t border-stroke': index > 0 }"
            >
              <div class="flex flex-col gap-0.5 items-start shrink-0 w-[140px] md:w-[180px]">
                <p class="font-sans font-medium text-sm text-dark truncate w-full">{{ order.name }}</p>
                <p class="font-sans text-[13px] text-muted">{{ formatDate(order.processedAt) }}</p>
              </div>
              <p class="font-sans text-[13px] text-secondary hidden md:block">
                {{ order.lineItemCount }} item{{ order.lineItemCount !== 1 ? 's' : '' }}
              </p>
              <p class="font-sans font-semibold text-[15px] text-dark">
                ${{ parseFloat(order.currentTotalPrice.amount).toFixed(2) }}
              </p>
              <div class="flex items-start px-2.5 py-1 rounded-full shrink-0" :class="statusBg(order.status)">
                <p class="font-sans font-medium text-xs text-secondary">{{ order.status }}</p>
              </div>
            </div>
          </template>
        </div>

        <!-- Saved Addresses -->
        <div class="bg-surface flex flex-col items-start p-7 rounded-xl w-full">
          <div class="flex items-center justify-between w-full">
            <p class="font-serif font-semibold text-[22px] text-dark">Saved Addresses</p>
            <button class="font-sans font-medium text-sm text-brand">+ Add New</button>
          </div>
          <div class="h-5" />

          <div v-if="addressesPending" class="flex items-center justify-center py-8 w-full">
            <p class="font-sans text-sm text-muted">Loading addresses…</p>
          </div>
          <div v-else-if="addresses.length === 0" class="flex items-center justify-center py-8 w-full">
            <p class="font-sans text-sm text-muted">No saved addresses.</p>
          </div>
          <div v-else class="flex flex-col md:flex-row gap-5 w-full">
            <div
              v-for="addr in addresses"
              :key="addr.id"
              class="bg-raised flex flex-1 flex-col gap-1.5 items-start p-4 rounded-lg"
            >
              <p class="font-sans font-medium text-sm text-brand">{{ addressLabel(addr) }}</p>
              <p class="font-sans text-[13px] text-secondary">{{ addr.address1 }}</p>
              <p v-if="addr.address2" class="font-sans text-[13px] text-secondary">{{ addr.address2 }}</p>
              <p class="font-sans text-[13px] text-secondary">{{ addressCity(addr) }}</p>
              <p class="font-sans text-[13px] text-secondary">{{ addr.country }}</p>
              <div class="h-1" />
              <p class="font-sans text-xs text-muted">Edit &nbsp;·&nbsp; Remove</p>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>
